import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  PlugsConnected, CheckCircle, PaintBrushBroad, Megaphone, ShieldCheck, ArrowRight,
  ChartLineUp, Stamp, Funnel, DeviceMobile, CalendarCheck, Robot, Sparkle,
} from "@phosphor-icons/react";
import { BrandMark } from "@/components/BrandMark";

const IMG = {
  hero: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtYXJrZXRpbmclMjBhbmFseXRpY3MlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzg2ODg2MDQwfDA&ixlib=rb-4.1.0&q=85&w=1600",
  riya: "https://static.prod-images.emergentagent.com/jobs/a7039436-a055-4978-8d02-9c6dc5d7b84b/images/79e24ad144c45b71cc14b0de6272069324978742bc4811d0dfed9afdd1993080.jpeg",
  arjun: "https://static.prod-images.emergentagent.com/jobs/a7039436-a055-4978-8d02-9c6dc5d7b84b/images/1a40819774853f045f46a4216cfb9ff31578dc8b8ead46a41ac8ff3585e7a11b.jpeg",
  meera: "https://static.prod-images.emergentagent.com/jobs/a7039436-a055-4978-8d02-9c6dc5d7b84b/images/8a6a2a09191ad63465e8e2000387fbed5991f028fbc5d693da6b36207c7870bc.jpeg",
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] } }),
};

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`px-6 md:px-12 lg:px-20 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#FFD8C4]/50 border border-[#FFD8C4] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-[#B84A1B]">
    <Sparkle size={12} weight="fill" className="text-[#FF6B35]" /> {children}
  </span>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#2D2422] font-figtree selection:bg-[#FF6B35] selection:text-white">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 border-b border-[#F0EBE1] bg-[#FAF9F6]/80 backdrop-blur-xl" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 h-16">
          <Link to="/" data-testid="landing-logo"><BrandMark size={30} /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#8a7f76]">
            <a href="#how" className="hover:text-[#2D2422] transition-colors" data-testid="landing-nav-how">How it works</a>
            <a href="#team" className="hover:text-[#2D2422] transition-colors" data-testid="landing-nav-team">AI Team</a>
            <a href="#features" className="hover:text-[#2D2422] transition-colors" data-testid="landing-nav-features">Features</a>
            <a href="#pricing" className="hover:text-[#2D2422] transition-colors" data-testid="landing-nav-pricing">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-semibold text-[#8a7f76] hover:text-[#2D2422] transition-colors px-3 py-2" data-testid="landing-login-btn">
              Log in
            </Link>
            <Link to="/signup" data-testid="landing-signup-btn"
              className="rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white text-sm font-bold px-5 py-2.5 transition-transform hover:-translate-y-0.5 active:scale-95 shadow-[0_8px_30px_rgba(255,107,53,0.25)]">
              Sign up free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <Section className="pt-36 md:pt-44 pb-20 relative overflow-hidden">
        <div className="absolute -top-24 -left-32 h-[420px] w-[420px] rounded-full bg-[#FFD8C4]/60 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-32 h-[380px] w-[380px] rounded-full bg-[#FFE9A8]/50 blur-3xl pointer-events-none" />
        <div className="relative text-center max-w-3xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
            <Eyebrow>AI-powered ads command center</Eyebrow>
          </motion.div>
          <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="font-outfit mt-7 text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02]">
            Your ads.<br /><span className="text-[#FF6B35]">On autopilot.</span>
          </motion.h1>
          <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
            className="mt-7 text-base md:text-lg text-[#6f645b] leading-relaxed max-w-xl mx-auto">
            5ads.ai connects your Meta ad accounts to a friendly three-person AI team that designs creatives,
            writes copy, optimizes budgets and hunts quality leads — you just approve.
          </motion.p>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/signup" data-testid="landing-cta-start"
              className="rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold px-8 py-4 text-sm inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95 shadow-[0_12px_40px_rgba(255,107,53,0.3)]">
              Start free <ArrowRight size={16} weight="bold" />
            </Link>
            <a href="#how" data-testid="landing-cta-how"
              className="rounded-full border border-[#E5DcD0] bg-white hover:border-[#FF6B35]/50 text-[#2D2422] font-semibold px-8 py-4 text-sm transition-colors">
              See how it works
            </a>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 flex flex-wrap justify-center gap-6 text-xs text-[#8a7f76]">
            {["Live Meta Marketing API", "You approve every action", "Guardrailed budgets"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" className="text-[#FF6B35]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 max-w-4xl mx-auto" data-testid="landing-hero-visual">
          <div className="rounded-3xl border border-[#F0EBE1] overflow-hidden bg-white p-2 shadow-[0_30px_80px_-20px_rgba(255,107,53,0.18)]">
            <img src={IMG.hero} alt="Ad performance analytics dashboard" className="w-full h-[300px] md:h-[420px] object-cover rounded-2xl" />
          </div>
          <div className="absolute -bottom-6 left-4 md:-left-8 rounded-2xl border border-[#F0EBE1] bg-white px-5 py-4 shadow-[0_8px_30px_rgba(255,107,53,0.12)]">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#b3a99e]">AI decision</div>
            <div className="mt-1 text-sm font-bold flex items-center gap-2">
              <ChartLineUp size={16} weight="bold" className="text-[#1a9e52]" /> Scale budget +20% — CPL down 31%
            </div>
          </div>
          <div className="absolute -top-5 right-4 md:-right-6 rounded-2xl border border-[#F0EBE1] bg-white px-4 py-3 shadow-[0_8px_30px_rgba(255,107,53,0.12)]">
            <div className="text-sm font-bold flex items-center gap-2">
              <Sparkle size={15} weight="fill" className="text-[#FF6B35]" /> 3 creatives awaiting approval
            </div>
          </div>
        </motion.div>
      </Section>

      {/* Marquee */}
      <div className="border-y border-[#F0EBE1] bg-[#FFD8C4]/25 py-5 overflow-hidden mt-8" data-testid="landing-marquee">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} className="flex shrink-0">
              {["META MARKETING API", "FACEBOOK ADS", "INSTAGRAM", "LEAD FORMS", "CLAUDE AI", "GEMINI IMAGES", "AD PREVIEWS", "BRAND KIT"].map((t) => (
                <span key={t + dup} className="mx-8 text-xs font-bold tracking-[0.3em] text-[#a4988c] inline-flex items-center gap-8">
                  {t} <span className="text-[#FF6B35]">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <Section id="how" className="py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Eyebrow>How it works</Eyebrow>
          <h2 className="font-outfit mt-5 text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            Connect. AI proposes. <span className="text-[#FF6B35]">You approve.</span>
          </h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-6" data-testid="landing-how-grid">
          {[
            { n: "01", icon: PlugsConnected, t: "Connect your accounts", d: "Log in with Facebook, pick the ad accounts you want managed, and 5ads.ai syncs campaigns, ads, audiences and leads live." },
            { n: "02", icon: Robot, t: "Your AI team proposes", d: "Every 3 hours the AI studies performance and lead quality, then drafts budget changes, new creatives and copy — with reasoning." },
            { n: "03", icon: CheckCircle, t: "You approve, it executes", d: "Actions wait in your approval queue. One click executes on real Meta campaigns within your guardrails." },
          ].map(({ n, icon: Icon, t, d }, i) => (
            <motion.div key={n} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="rounded-3xl bg-white border border-[#F0EBE1] p-9 shadow-[0_8px_30px_rgba(255,107,53,0.06)] transition-transform hover:-translate-y-1.5">
              <div className="flex items-center justify-between">
                <span className="h-12 w-12 rounded-2xl bg-[#FFD8C4]/50 flex items-center justify-center">
                  <Icon size={22} weight="duotone" className="text-[#FF6B35]" />
                </span>
                <span className="font-outfit text-5xl font-extrabold tracking-tighter text-[#FFD8C4]">{n}</span>
              </div>
              <h3 className="font-outfit mt-7 text-xl font-bold tracking-tight">{t}</h3>
              <p className="mt-3 text-sm text-[#6f645b] leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* AI Team */}
      <Section id="team" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-10 right-0 h-[340px] w-[340px] rounded-full bg-[#FFD8C4]/40 blur-3xl pointer-events-none" />
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative">
          <Eyebrow>Meet your AI team</Eyebrow>
          <h2 className="font-outfit mt-5 text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            Three friendly teammates. <span className="text-[#FF6B35]">Zero payroll.</span>
          </h2>
          <p className="mt-4 text-base text-[#6f645b] max-w-xl leading-relaxed">
            Design → copy & media plan → senior review. Each stage passes through you before anything reaches Meta.
            Chat with any teammate for redesigns — even attach competitor ads as references.
          </p>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-8 relative">
          {[
            { img: IMG.riya, icon: PaintBrushBroad, name: "Riya", role: "Designer", d: "Generates scroll-stopping ad images in your brand style, with your real logo stamped pixel-perfect.", tilt: "hover:-rotate-1" },
            { img: IMG.arjun, icon: Megaphone, name: "Arjun", role: "Ads Manager", d: "Writes headlines and primary text, picks the best ad set, and sets budgets within your guardrails.", tilt: "hover:rotate-1" },
            { img: IMG.meera, icon: ShieldCheck, name: "Meera", role: "Senior Reviewer", d: "Stress-tests every ad against your data and sends weak work back before you ever see it.", tilt: "hover:-rotate-1" },
          ].map(({ img, icon: Icon, name, role, d, tilt }, i) => (
            <motion.div key={name} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className={`rounded-3xl bg-white border border-[#F0EBE1] overflow-hidden shadow-[0_8px_30px_rgba(255,107,53,0.08)] transition-transform hover:-translate-y-2 ${tilt}`}
              data-testid={`landing-teammate-${name.toLowerCase()}`}>
              <div className="relative">
                <img src={img} alt={`${name}, AI ${role}`} className="w-full h-56 object-cover object-top" />
                <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6B35]">
                  <Icon size={13} weight="fill" /> {role}
                </span>
              </div>
              <div className="p-7">
                <h3 className="font-outfit text-xl font-bold tracking-tight">{name}</h3>
                <p className="mt-2 text-sm text-[#6f645b] leading-relaxed">{d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Deep features */}
      <Section id="features" className="py-24 md:py-32">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
          <Eyebrow>Built for performance</Eyebrow>
          <h2 className="font-outfit mt-5 text-4xl md:text-5xl font-bold tracking-tight max-w-3xl">
            Everything an ads team does — <span className="text-[#FF6B35]">automated, audited, approved.</span>
          </h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-12 gap-6" data-testid="landing-features-grid">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="md:col-span-7 rounded-3xl bg-[#2D2422] text-[#FAF9F6] p-9 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#FF6B35]/25 blur-3xl" />
            <ChartLineUp size={26} weight="duotone" className="text-[#FF9A6C]" />
            <h3 className="font-outfit mt-4 text-2xl font-bold tracking-tight">Live Meta API sync</h3>
            <p className="mt-3 text-sm text-[#d8cfc6] leading-relaxed max-w-md">
              Campaigns, ads, ad sets, audiences and every lead — synced automatically every 3 hours with 31 days of daily insights.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["Campaigns", "Ad sets", "Creatives", "Leads", "Audiences"].map((t) => (
                <span key={t} className="rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold">{t}</span>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="md:col-span-5 rounded-3xl bg-white border border-[#F0EBE1] p-9 shadow-[0_8px_30px_rgba(255,107,53,0.06)]">
            <span className="h-12 w-12 rounded-2xl bg-[#FFD8C4]/50 flex items-center justify-center">
              <Stamp size={22} weight="duotone" className="text-[#FF6B35]" />
            </span>
            <h3 className="font-outfit mt-4 text-xl font-bold tracking-tight">Brand Kit stamping</h3>
            <p className="mt-2 text-sm text-[#6f645b] leading-relaxed">
              AI never draws your logo. Your real logo file is composited programmatically onto every generated creative — pixel-perfect, every time.
            </p>
          </motion.div>
          {[
            { icon: Funnel, t: "Lead quality scoring", d: "Every lead scored Hot / Warm / Cold so your sales team calls the right people first." },
            { icon: DeviceMobile, t: "Real ad previews", d: "Pixel-faithful Facebook and Instagram feed mockups of every AI creative before it ships." },
            { icon: CalendarCheck, t: "Weekly creative calendar", d: "A 7-day creative plan across your campaigns. Edit any day, or let it run on schedule." },
          ].map(({ icon: Icon, t, d }, i) => (
            <motion.div key={t} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="md:col-span-4 rounded-3xl bg-white border border-[#F0EBE1] p-8 shadow-[0_8px_30px_rgba(255,107,53,0.06)] transition-transform hover:-translate-y-1.5">
              <span className="h-11 w-11 rounded-2xl bg-[#FFD8C4]/50 flex items-center justify-center">
                <Icon size={20} weight="duotone" className="text-[#FF6B35]" />
              </span>
              <h3 className="font-outfit mt-4 text-lg font-bold tracking-tight">{t}</h3>
              <p className="mt-2 text-sm text-[#6f645b] leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Pricing */}
      <Section id="pricing" className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute -top-20 -right-24 h-[360px] w-[360px] rounded-full bg-[#FFD8C4]/40 blur-3xl pointer-events-none" />
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="relative">
          <Eyebrow>Pricing</Eyebrow>
          <h2 className="font-outfit mt-5 text-4xl md:text-5xl font-bold tracking-tight max-w-2xl">
            Simple plans. <span className="text-[#FF6B35]">Pay as you grow.</span>
          </h2>
          <p className="mt-4 text-base text-[#6f645b] max-w-xl leading-relaxed">
            Every plan sees everything — dashboards, leads, reports and unlimited WhatsApp speed-to-lead are free forever. Paid plans unlock the AI that does the work for you.
          </p>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-6 relative" data-testid="landing-pricing-grid">
          {[
            { key: "free", name: "Free", price: "₹0", blurb: "See everything, free forever", popular: false,
              features: ["1 ad account · 2 team seats", "All dashboards, leads & reports — free forever", "Unlimited WhatsApp speed-to-lead", "Zoho CRM sync + Conversions API", "Starter AI quotas (manual runs only)"] },
            { key: "pro", name: "Pro", price: "₹2,499", blurb: "For growing advertisers", popular: true,
              features: ["3 ad accounts · unlimited team seats", "Everything in Free", "Full monthly AI quotas + top-ups", "Autopilot mode + weekly creative calendar"] },
            { key: "agency", name: "Agency", price: "₹7,999", blurb: "For teams & agencies", popular: false,
              features: ["Unlimited ad accounts", "Everything in Pro", "Highest AI quotas on every feature", "Priority support"] },
          ].map(({ key, name, price, blurb, popular, features }, i) => (
            <motion.div key={key} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              data-testid={`landing-plan-${key}`}
              className={`rounded-3xl bg-white p-9 flex flex-col border transition-transform hover:-translate-y-1.5 ${
                popular ? "border-[#FF6B35] shadow-[0_20px_60px_rgba(255,107,53,0.16)]" : "border-[#F0EBE1] shadow-[0_8px_30px_rgba(255,107,53,0.06)]"}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-outfit text-xl font-bold tracking-tight">{name}</h3>
                {popular && (
                  <span className="rounded-full bg-[#FF6B35] text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1">Most popular</span>
                )}
              </div>
              <div className="font-outfit mt-4 text-4xl font-extrabold tracking-tight">
                {price}<span className="text-sm font-semibold text-[#a4988c]">/mo</span>
              </div>
              <p className="text-sm text-[#a4988c] mt-1">{blurb}</p>
              <ul className="mt-6 space-y-3 flex-1">
                {features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#6f645b]">
                    <CheckCircle size={16} weight="fill" className="text-[#FF6B35] shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/signup" data-testid={`landing-plan-cta-${key}`}
                className={`mt-8 rounded-full text-center font-bold px-6 py-3.5 text-sm transition-transform hover:-translate-y-0.5 active:scale-95 ${
                  popular ? "bg-[#FF6B35] hover:bg-[#E85A25] text-white shadow-[0_8px_30px_rgba(255,107,53,0.25)]"
                          : "border border-[#E5DCD0] hover:border-[#FF6B35]/50 text-[#2D2422] bg-white"}`}>
                {key === "free" ? "Start free" : `Start with ${name}`}
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Footer CTA */}
      <footer className="bg-[#2D2422] text-[#FAF9F6]">
        <Section className="py-24 md:py-32">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
            <h2 className="font-outfit text-5xl md:text-7xl font-extrabold tracking-tight leading-[1.0]">
              Ready to scale?<br />
              <span className="text-[#FF6B35]">Your AI team is waiting.</span>
            </h2>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="landing-footer-cta"
                className="rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold px-8 py-4 text-sm inline-flex items-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95">
                Create your workspace <ArrowRight size={16} weight="bold" />
              </Link>
              <Link to="/login" className="text-sm font-semibold text-[#c9beb3] hover:text-white transition-colors" data-testid="landing-footer-login">
                Already have an account? Log in
              </Link>
            </div>
          </motion.div>
          <div className="mt-20 pt-8 border-t border-white/10">
            <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-[#a4988c]">
              <BrandMark size={24} dark />
              <span className="flex items-center gap-4">
                <Link to="/privacy" className="hover:text-white transition-colors" data-testid="landing-footer-privacy">Privacy Policy</Link>
                <Link to="/terms" className="hover:text-white transition-colors" data-testid="landing-footer-terms">Terms of Service</Link>
                <span>© {new Date().getFullYear()} 5ads.ai. Built for Meta advertisers.</span>
              </span>
            </div>
            <p className="mt-6 text-[11px] leading-relaxed text-[#FAF9F6]/50 max-w-3xl" data-testid="landing-disclaimer">
              Disclaimer: 5ads.ai is an independent software tool and is not affiliated with, sponsored by, or endorsed by
              Meta Platforms, Inc. or Google LLC. Facebook, Instagram and Meta are trademarks of Meta Platforms, Inc.
              Gemini is a trademark of Google LLC. All product names, logos and brands are property of their respective owners.
            </p>
          </div>
        </Section>
      </footer>
    </div>
  );
}
